import { useOrganization } from "@/lib/organization/organization-context"
import { useCampaignResults } from "@/lib/api/use-campaigns"
import type { CampaignResult } from "@/lib/api/campaigns"
import { formatMoney } from "@/lib/service-lane/baseline-math"
import { ChartCard } from "./room-chrome"

// "Which sequence converts" — the observational read Sam wanted on the
// dashboard. Self-contained (fetches its own team-scoped data), so it drops
// into the Converting chapter with a single line. Plain outcome language, no
// dials to tune.
export function CampaignResultsCard() {
  const { activeDealershipId, activeBodyShopId, activeTeamKind } = useOrganization()
  // Scope to the SAME team the dashboard is showing, not always body-shop-first
  // — otherwise the card could report a different location than the rest of the
  // page (F31).
  const teamId =
    activeTeamKind === "bodyshop" ? activeBodyShopId : activeDealershipId
  const { data } = useCampaignResults(teamId ?? null)
  const rows = (data ?? []).filter((c) => c.enrolled > 0)
  if (rows.length === 0) return null

  const digest = rollupDigest(rows)
  const takeaway = abTakeaway(rows)

  return (
    // ChartCard idiom — same shell + warm palette as its sibling charts in the
    // Converting chapter, instead of a bespoke card (F32).
    <ChartCard
      title="Follow-up sequence performance"
      sub="How the automated sequences we run are converting confirmed estimates."
    >
      {/* Plain-language rollup — what the follow-up actually did (#26). */}
      <p className="text-[12.5px] text-zinc-700 leading-snug">{digest}</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {rows.map((c) => (
          <CampaignRow key={c.campaignId} c={c} />
        ))}
      </div>
      {/* Which sequence is winning, stated in a sentence (#27). */}
      {takeaway && (
        <p className="mt-3 pt-3 border-t border-zinc-100 text-[12px] text-zinc-600 leading-snug">
          <span className="font-semibold text-zinc-800">Takeaway — </span>
          {takeaway}
        </p>
      )}
    </ChartCard>
  )
}

// Rollup across both sequences, in outcome language (no invented metrics —
// every number is a real funnel count, and the money is the booked rows' value).
function rollupDigest(rows: CampaignResult[]): string {
  const sum = (k: "enrolled" | "replied" | "scheduled" | "converted" | "bookedValue") =>
    rows.reduce((n, c) => n + c[k], 0)
  const enrolled = sum("enrolled")
  const replied = sum("replied")
  const booked = sum("scheduled")
  const converted = sum("converted")
  const bookedValue = sum("bookedValue")
  const parts = [
    `reached ${enrolled} confirmed ${enrolled === 1 ? "estimate" : "estimates"}`,
  ]
  if (replied > 0) parts.push(`${replied} replied`)
  if (booked > 0)
    parts.push(
      `${booked} booked${bookedValue > 0 ? ` worth ${formatMoney(bookedValue)}` : ""}`,
    )
  if (converted > 0) parts.push(`${converted} completed`)
  return `Automated follow-up ${parts.join(", ")}.`
}

// Text-first vs email-only, decided by conversion rate (#27).
function abTakeaway(rows: CampaignResult[]): string | null {
  if (rows.length < 2) return null
  const rate = (c: CampaignResult) =>
    c.enrolled > 0 ? c.converted / c.enrolled : 0
  const [win, lose] = [...rows].sort((a, b) => rate(b) - rate(a))
  const wr = Math.round(rate(win) * 100)
  const lr = Math.round(rate(lose) * 100)
  if (wr === lr)
    return `both sequences are converting about the same (${wr}%).`
  return `${win.name} is converting better — ${wr}% vs ${lr}%.`
}

function CampaignRow({ c }: { c: CampaignResult }) {
  const conv = c.enrolled > 0 ? Math.round((c.converted / c.enrolled) * 100) : 0
  const stages = [
    { label: "Enrolled", v: c.enrolled },
    { label: "Replied", v: c.replied },
    { label: "Booked", v: c.scheduled },
    { label: "Converted", v: c.converted },
  ]
  const max = c.enrolled || 1

  return (
    <div className="rounded-xl border border-zinc-200/60 bg-zinc-50/40 p-3.5">
      <div className="flex items-baseline justify-between gap-2 mb-2.5">
        <p className="text-[13px] font-semibold text-zinc-800">{c.name}</p>
        <p className="text-[14px] font-bold text-emerald-700 tabular-nums">
          {conv}%
          <span className="text-[10.5px] font-medium text-zinc-400"> converted</span>
        </p>
      </div>
      <div className="flex items-end gap-1.5 h-16">
        {stages.map((s) => (
          <div key={s.label} className="flex-1 flex flex-col items-center justify-end gap-1">
            <span className="text-[11px] font-semibold text-zinc-700 tabular-nums">
              {s.v}
            </span>
            <div
              className="w-full rounded-t bg-emerald-400/80"
              style={{ height: `${Math.max(4, (s.v / max) * 100)}%` }}
            />
            <span className="text-[9.5px] text-zinc-400">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
